<template>
  <div class="row inbox-component parent">
    <div class="col-3">
      <ConversationTimeline
        v-if="showTimeline"
        ref="ConversationTimeline"
        :store="store"
        :selectedConversationId="selectedConversationId"
      />
    </div>
    <div :class="['h-100', showTimeline ? 'col-9': 'col-12' ]">
      <label class="input-toggle float-right">
        <input v-model="galleryView" type="checkbox">
        <span class="toggle-slider" /> Media View
      </label>
      <p>
        Time Zone: {{ inboxHelper.getFriendlyTimezoneName() }}
      </p>
      <div>
        <div class="position-relative">
          <div ref="inbox" class="inbox clearfix" @scroll="handleMessageScroll">
            <button
              v-if="!galleryView"
              :class="['jump-to-present btn btn-default fas fa-arrow-down', {visible: isAboveBottom}]"
              aria-label="Jump to present"
              @click="goToBottom()"
            />
            <div class="position-relative">
              <GalleryView
                v-if="galleryView"
                :messages-with-images="messagesWithImages"
                :store="store"
                :inbox-helper="inboxHelper"
                :show-load-more="store.loading.older"
                @openImageLightbox="openImageLightbox"
              />
              <MessageView
                v-else
                ref="messageView"
                :messages-by-date="messagesByDate"
                :store="store"
                :inbox-helper="inboxHelper"
                :show-load-more="store.loading.older"
                :show-ai-icon="!readOnly && aiSuggestionsEnabled"
                @openImageLightbox="openImageLightbox"
                @suggestResponse="suggestResponse"
              />
            </div>
          </div>
          <ImageLightbox
            ref="imageLightbox"
            v-model="showImageLightbox"
            :store="store"
          />
        </div>
      </div>
      <hr>
      <MessagingModeSwitcher
        :manual-mode-helper="manualModeHelper"
        :store="store"
        :manual-mode-enabled="manualModeEnabled"
        :reply-window-enabled="replyWindowEnabled"
        :reply-window-configuration="replyWindowConfiguration"
        :has-pending-texts="hasPendingTexts"
      />

      <AiResponseRequestRejectModal
        :store="store"
        @submit="rejectSuggestedResponse"
      />
      <div v-if="!readOnly">
        <InputArea
          v-model="textContent"
          :canned-responses="store.cannedResponses"
          :disabled="disableTextInput"
          :show-image-upload-invoker="!imageUrl"
          :attach-image="attachImage"
          :token-url="tokenUrl"
        />
        <div v-if="!store.aiGeneratedResponse" class="inbox-action-items">
          <div v-if="imageUrl">
            <h4>Attachments</h4>
            <ul class="list-unstyled">
              <li>
                <span class="filename">{{ imageName }}</span>
                <button
                  class="btn btn-link"
                  type="button"
                  @click="removeAttachment"
                >
                  <span class="fa fa-trash text-danger" />
                  <span class="sr-only">Remove</span>
                </button>
              </li>
            </ul>
          </div>
          <input
            v-if="!store.loading.send"
            type="submit"
            value="Send"
            :class="styleConfig.inboxSubmit"
            :disabled="store.loading.send"
            @click="sendMessage"
          >

          <div
            v-else
            :class="styleConfig.inboxSubmit"
            class="disabled"
          >
            Send
            <b-spinner
              small
              variant="light"
              label="Spinning"
            />
          </div>

          <button
            v-if="aiSuggestionsEnabled && store.selectedMessage && !store.loading.suggestResponse"
            type="submit"
            :class="[styleConfig.inboxSuggestResponse, 'suggest-response-button', {'selected': store.selectedMessage, 'loading': store.loading.suggestResponse}]"
            :disabled="store.loading.suggestResponse && !store.selectedMessage"
            @click="suggestResponse"
          >
            Suggest Response
            <b-icon icon="stars" class="ml-1" />
          </button>
          <div
            v-else-if="aiSuggestionsEnabled && !store.selectedMessage || store.loading.suggestResponse"
            id="ai-response-info"
            :class="[styleConfig.inboxSuggestResponse, 'suggest-response-button', {'selected': store.selectedMessage, 'loading': store.loading.suggestResponse}]"
          >
            Suggest Response
            <b-spinner
              v-if="store.loading.suggestResponse"
              small
              variant="light"
              label="Spinning"
            />
            <span v-if="!store.selectedMessage">
              <b-icon
                id="ai-response-info-icon"
                icon="stars"
                variant="light"
                font-scale="1"
                class="ml-1"
              />
            </span>
            <b-popover
              target="ai-response-info"
              title="AI generated responses are enabled"
              triggers="click blur"
              placement="right"
            >
              Select an inbound message above and click "Suggest Response" to generate a response.
            </b-popover>
          </div>
        </div>
        <div v-else class="inbox-action-items mt-1">
          <b-button
            variant="primary"
            :disabled="store.loading.send"
            @click="sendSuggestedResponse"
          >
            <b-icon icon="hand-thumbs-up" />
            Send
          </b-button>
          <b-button
            variant="danger"
            :disabled="store.loading.send"
            @click="openRejectCommentModal"
          >
            <b-icon icon="hand-thumbs-down" />
            Reject
          </b-button>
          <b-button
            variant="secondary"
            :disabled="store.loading.send"
            :class="{'refresh-button': true, 'selected': store.selectedMessage, 'loading': store.loading.refreshResponse}"
            @click="refreshSuggestedResponse"
          >
            <b-icon icon="arrow-clockwise" />
            Refresh
            <b-spinner
              v-if="store.loading.refreshResponse"
              small
              variant="light"
              label="Spinning"
            />
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import InboxStore from "./stores/inbox";
import styles from './stores/styles';
import inboxHelper from './helpers/inbox';
import manualModeHelper from './helpers/manualMode';
import ImageLightbox from "./components/ImageLightbox.vue";
import InputArea from "@/FormWidgets/WidgetInputArea/InputArea.vue";
import GalleryView from "./components/GalleryView.vue";
import MessageView from "./components/MessageView.vue";
import AiResponseRequestRejectModal from "./components/AiResponseRequestRejectModal.vue";
import ConversationTimeline from "@/Inbox/components/ConversationTimeline.vue";
import MessagingModeSwitcher from "@/Inbox/components/MessagingModeSwitcher.vue";
import {throttle} from "lodash-es";

export default {
  name: "InboxComponent",
  components: {
    MessagingModeSwitcher,
    ConversationTimeline,
    GalleryView,
    InputArea,
    ImageLightbox,
    MessageView,
    AiResponseRequestRejectModal
  },
  props: {
    auth: {
      type: Object,
      required: false,
      default() {
        return {
          method: "session",
          apiKey: ""
        }
      }
    },
    pollFrequencySeconds: {
      type: Number,
      required: false,
      default: 10,
    },
    apiBaseUrl: {
      type: String,
      required: true
    },
    studyId: {
      type: Number,
      required: true
    },
    participantId: {
      type: Number,
      required: true
    },
    resource: {
      type: String,
      default: "participants",
      validator: function (value) {
        // The value must match one of these
        return ['participants', 'support_partners'].includes(value);
      },
    },
    manualModeEnabled: {
      type: Boolean,
      default: false,
    },
    replyWindowEnabled: {
      type: Boolean,
      default: false,
    },
    replyWindowConfiguration: {
      type: [Object, Array],
      default() {
        return {};
      }
    },
    aiSuggestionsEnabled: {
      type: Boolean,
      default: false,
    },
    conversationTimelineEnabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    tokenUrl: {
      type: String,
      required: true,
    },
    styles: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      selectedConversationId: 0,
      styleConfig: Object.assign(styles, this.styles),
      store: new InboxStore(
          this.apiBaseUrl,
          this.participantId,
          this.resource,
          this.studyId,
          this.auth
      ),
      inboxHelper,
      manualModeHelper,
      textContent: "",
      imageUrl: "",
      imageName: "",
      showImageLightbox: false,
      galleryView: false,
      throttledHandler: null,
      throttledLoadOlderHandler: null,
      atPageBottom: true,
      scrollLoadThreshold: 0.2, // Trigger loading when 20% from top
      isLoadingOlderMessages: false,
      isSmoothScrolling: false,
    }
  },
  computed: {
    isReducedMotion() {
      return window.matchMedia(`(prefers-reduced-motion: reduce)`) === true
      || window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;
    },
    isAboveBottom() {
      return !this.atPageBottom;
    },
    disableTextInput() {
      return Boolean(this.store.loading.send)
          || Boolean(this.store.loading.suggestResponse)
          || Boolean(this.store.loading.refreshResponse);
    },
    hasMessages(){
      return Object.keys(this.store.messagesObj).length > 0;
    },
    showTimeline(){
      return this.conversationTimelineEnabled && !this.galleryView;
    },
    sortedMessages() {
      if (this.hasMessages) {
        return this.inboxHelper.sortMessages(Object.values(this.store.messagesObj));
      }
      return [];
    },
    messagesByDate() {
      if (this.sortedMessages.length) {
        return this.sortedMessages.reduce(function (rv, x) {
          (rv[x['created_at'].split(" ")[0]] = rv[x['created_at'].split(" ")[0]] || []).push(x);
          return rv;
        }, {});
      }
      return [];
    },
    messagesWithImages() {
      return Object.values(this.store.messagesObj).filter(msg => msg.media.length > 0).reverse();
    },
    latestMessageId() {
      // Note that if this.store.messagesObj is set to {}, this will return -Infinity
      return Math.max(...Object.keys(this.store.messagesObj));
    },
    hasPendingTexts() {
      if (this.messagesByDate.length > 0) {
        return this.manualModeHelper.messagesQueuedSinceManualMode(this.messagesByDate).length > 0;
      }
      return false;
    }
  },
  watch: {
    latestMessageId() {
      this.$nextTick(() => {
        this.scrollToNewest('smooth');
      });
    },
    galleryView() {
      // scroll to newest when changing views
      // todo: I think we need to reload in the newest messages into this view
      if(this.galleryView) {
        this.$nextTick(() => {
          this.scrollToNewest();
        });
      }
    }
  },
  created() {
    this.throttledHandler = throttle(() => this.store.resetManualModeTimeoutCounter(), 1000);
    document.addEventListener('mousemove', this.throttledHandler);
    document.addEventListener('mouseup', this.throttledHandler);
    document.addEventListener('keyup', this.throttledHandler);
    document.addEventListener('touchend', this.throttledHandler);
    document.addEventListener('touchmove', this.throttledHandler);

    this.throttledLoadOlderHandler = throttle((inboxElement) => {
        this.loadOlderMessages(inboxElement);
    }, 200);
  },
  beforeDestroy() {
    if (this.throttledHandler) {
      this.throttledHandler.cancel();
    }
    if (this.throttledLoadOlderHandler) {
      this.throttledLoadOlderHandler.cancel();
    }
    document.removeEventListener('mousemove', this.throttledHandler);
    document.removeEventListener('mouseup', this.throttledHandler);
    document.removeEventListener('keyup', this.throttledHandler);
    document.removeEventListener('touchend', this.throttledHandler);
    document.removeEventListener('touchmove', this.throttledHandler);
  },
  async mounted(){
    await this.store.loadMessages();
    this.scrollToNewest();
    this.poll();
    await this.store.getCannedResponses();
  },
  methods: {
    attachImage(url, name = "Attachment") {
      this.imageUrl = url;
      this.imageName = name;
    },
    removeAttachment() {
      this.imageUrl = '';
      this.imageName = '';
    },
    openImageLightbox(msgId, imageIndex) {
      this.showImageLightbox = true;
      this.$refs.imageLightbox.open(msgId, imageIndex, this.galleryView);
    },
    openRejectCommentModal() {
      this.$bvModal.show('reject-comment-modal');
    },
    goToBottom() {
      this.$nextTick(() => {
        this.scrollToNewest('smooth');
        this.$refs.inbox?.focus({preventScroll: true});
      });
    },
    handleMessageScroll(e) {
      const inboxElement = e.target;
      const {scrollTop, offsetHeight, scrollHeight} = inboxElement;
      this.atPageBottom = (scrollTop + offsetHeight) + 300 >= scrollHeight;
      if (this.atPageBottom) {
        this.isSmoothScrolling = false;
      }

      if (this.galleryView) {
        this.handleGalleryScroll(inboxElement);
        return;
      }

      if (scrollHeight > 0 && !this.isSmoothScrolling) {
        const scrollPercentage = scrollTop / scrollHeight;

        // Check if we're at 20% threshold and not already doing a merge/scroll
        if (scrollPercentage <= this.scrollLoadThreshold && !this.store.loading.older && !this.isLoadingOlderMessages) {
          // Use throttled handler for loading check to prevent excessive calls
          this.isLoadingOlderMessages = true;
          if (this.throttledLoadOlderHandler) {
            this.throttledLoadOlderHandler(inboxElement);
          }
          this.isLoadingOlderMessages = false;
        }
      }
    },
    async handleGalleryScroll(inboxElement) {
      if (this.store.loading.older) {
        return;
      }

      const {scrollTop, offsetHeight, scrollHeight} = inboxElement;
      if (scrollHeight > 0 && scrollTop >= scrollHeight - offsetHeight - 1) {
        await this.store.loadOlderLegacy();
      }
    },
    async loadOlderMessages(inboxElement) {
      const hasPending = await this.store.loadOlder();
      if (!hasPending) {
        return;
      }

      const scrollTopBefore = inboxElement.scrollTop;
      const scrollHeightBefore = inboxElement.scrollHeight;

      this.store.mergePendingOlderMessages();

      const scrollHeightAfter = inboxElement.scrollHeight;
      const heightDifference = scrollHeightAfter - scrollHeightBefore;

      const targetScrollTop = scrollTopBefore + heightDifference;
      inboxElement.scrollTop = Math.max(0, targetScrollTop);
    },
    async scrollToNewest(behavior = 'auto') {
      if (behavior === 'smooth') {
        this.isSmoothScrolling = true;
      }
      const el = this.$refs.inbox;
      const scrollToOffset = this.galleryView ? 0 : el.scrollHeight;
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({top: scrollToOffset, behavior: this.isReducedMotion ? 'auto' : behavior});
      } else {
        // IE fallback
        el.scrollTop = scrollToOffset;
      }
    },
    async sendMessage() {
      if (this.textContent.length || this.imageUrl) {
        const success = await this.store.sendMessage(this.textContent, this.imageUrl);

        if (!success) {
          return;
        }

        if (this.manualModeEnabled && this.manualModeHelper.isAutomatedModeActive) {
          await this.store.enableManualMode();
        }

        this.scrollToNewest('smooth');
        this.textContent = "";
        this.imageUrl = '';
        this.imageName = '';
      }
    },
    async sendSuggestedResponse() {
      if (this.textContent.length && this.store.aiGeneratedResponse) {
        await this.store.sendSuggestedMessage(this.textContent, this.imageUrl);
        this.$refs.messageView?.clearSelection();
        this.scrollToNewest('smooth');
        this.textContent = "";
        this.imageUrl = '';
        this.imageName = '';
      }
    },
    async rejectSuggestedResponse(comment) {
      if (this.store.aiGeneratedResponse) {
        await this.store.rejectSuggestedMessage(comment);
        this.$refs.messageView?.clearSelection();
        this.textContent = "";
        this.imageUrl = '';
        this.imageName = '';
        this.$bvModal.hide('reject-comment-modal');
      }
    },
    async refreshSuggestedResponse() {
      await this.store.refreshSuggestedMessage();
      this.textContent = this.store.aiGeneratedResponse.response;
    },
    async suggestResponse() {
      await this.store.suggestResponse();
      this.textContent = this.store.aiGeneratedResponse.response;
    },
    poll() {
      setTimeout(async () => {
        await this.store.poll();
        this.poll();
      }, this.pollFrequencySeconds * 1000)
    },
  },
}
</script>

<style scoped>
.parent {
  overflow: hidden;
}

.inbox {
  max-height: 60vh;
  min-height: 60vh;
  overflow-x: hidden;
  overflow-y: scroll;
  border-bottom: 2px solid rgba(0, 0, 0, .2);
  /* Give space for jump button, so it doesn't show up over message text */
  padding-right: 10px;
}

.link-container {
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
}

@media print {
  /* Note: this reveals all the currently loaded messages */
  .parent, .inbox {
    max-height: none;
    height: auto;
  }
}

::v-deep .time {
  float: right;
  margin: 0 3px 0;
  color: gray;
}

::v-deep .status-icon {
  float: left;
  cursor: pointer;
  margin: 0 3px 0;
}

::v-deep .cursor-pointer {
  cursor: pointer;
}

::v-deep .suggest-response-button {
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

::v-deep .suggest-response-button.selected {
  background: linear-gradient(135deg,
    #6366f1 0%,
    #8b5cf6 25%,
    #d1d5db 50%,
    #6366f1 75%,
    #8b5cf6 100%
  );
  background-size: 400% 400%;
  color: white;
}

::v-deep .suggest-response-button.selected.loading {
  animation: gradientShift 3s ease-in-out infinite;
}

@keyframes gradientShift {
  0% {
    background-position: 0 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

::v-deep .refresh-button {
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

::v-deep .refresh-button.selected {
  background: linear-gradient(135deg,
    #6366f1 0%,
    #8b5cf6 25%,
    #d1d5db 50%,
    #6366f1 75%,
    #8b5cf6 100%
  );
  background-size: 400% 400%;
  color: white;
}

::v-deep .refresh-button.selected.loading {
  animation: gradientShift 3s ease-in-out infinite;
}

.jump-to-present {
  text-align: center;
  position: absolute;
  bottom: 30px;
  right: 10px;
  /* Put above MessageView items: */
  z-index: 1;
  translate: 100px 0;

  width: 36px;
  height: 36px;
  font-size: 14px;
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: .4s translate;
  @media (prefers-reduced-motion: reduce) {
    transition: 0.01s translate
  }

  &:focus {
    transition: none;
    translate: 0 0;
    bottom: 30px;
    right: 10px;
  }

  /* Used in computed.isAboveBottom */

  &.visible {
    translate: 0 0;
  }
}
</style>
